


import Vue from 'vue';
import '../../../../node_modules/lightgallery.js/dist/js/lightgallery';
import i18n from "@/i18n/i18n.config";
import {OfferItem, Status, TranslatedString} from "@/components/model/offer-item";
import {RouteName} from "@/router";
import {item} from "@/components/items/details/item.service";
import {pageTitle} from "@/components/common/pageTitle";

declare var lightGallery: any;

interface Data {
    item: OfferItem | null
    isLoading: boolean
    galleryInitialized: boolean
    galleryElement: Element | null
}

export default Vue.extend({
    props: {
        itemId: String
    },
    data: (): Data => ({
        item: null,
        isLoading: false,
        galleryInitialized: false,
        galleryElement: null
    }),
    created(): void {
        this.loadItem(this.itemId);
    },
    watch: {
        itemId(newItemId: string) {
            this.loadItem(newItemId);
            this.galleryInitialized = false;
        }
    },
    updated(): void {
        if (!this.isLoading && !this.galleryInitialized) {
            this.galleryElement = document.querySelector('.gallery');
            lightGallery(this.galleryElement, {
                selector: '.item',
                mode: 'lg-fade',
                speed: 200,
                download: false
            });
            this.galleryInitialized = true;
        }
    },
    beforeRouteLeave(to, from, next) {
        if (this.galleryInitialized && this.galleryElement) {
            // @ts-ignore
            let gallery = window.lgData[this.galleryElement.getAttribute('lg-uid')];
            gallery.destroy();
            this.galleryInitialized = false;
        }
        next();
    },
    computed: {
        isArchive(): boolean {
            return this.$route.meta.archive;
        },
        mainPictureUrl(): string | undefined {
            if (!this.item || !this.item.pictures.length) {
                return undefined;
            }
            const mainPicture = this.item.pictures.find(picture => picture.main);
            if (mainPicture) {
                return mainPicture.url;
            } else {
                return undefined;
            }
        },
        smallPictures(): string[] {
            if (!this.item || this.item.pictures.length <= 1) {
                return [];
            }
            return this.item.pictures.slice(1, 4)
                .map(picture => picture.url);
        },
        showMorePicture(): string | undefined {
            if (!this.item || this.item.pictures.length <= 4) {
                return undefined;
            }
            return this.item.pictures[4].url;
        },
        hiddenPictures(): string[] {
            if (!this.item || this.item.pictures.length <= 5) {
                return [];
            }
            return this.item.pictures.slice(5, this.item.pictures.length)
                .map(picture => picture.url);
        }
    },
    methods: {
        loadItem(itemId: string) {
            this.isLoading = true;
            item.loadById(this.parseItemId(itemId))
                .then(item => this.item = item)
                .then(() => this.validateItem())
                .then(() => {
                    if (this.item) {
                        pageTitle.setTitle(this.item.name.pl);
                    }
                })
                .catch(response => {
                    if (response.status === 404) {
                        this.$router.replace({name: RouteName.notFound});
                    }
                })
                .finally(() => this.isLoading = false);
        },
        parseItemId(itemId: string): number {
            const parsed = parseInt(itemId);
            if (isNaN(parsed)) {
                const splitted = itemId.split('/');
                return parseInt(splitted[splitted.length - 1]);
            } else {
                return parsed;
            }
        },
        validateItem() {
            if (!this.item) {
                return;
            }
            const thisItemRouteParams = {itemName: this.item.urlFriendlyName, itemId: this.itemId};
            const archiveItemRouteParams = {...thisItemRouteParams, pageNumber: '0'};
            if (this.isArchive && this.item.status === Status.ACTIVE) {
                this.$router.replace({name: RouteName.offerDetails, params: thisItemRouteParams});
            }
            if (!this.isArchive && this.item.status !== Status.ACTIVE) {
                this.$router.replace({name: RouteName.archiveDetails, params: archiveItemRouteParams});
            }
            const routeName = this.$router.currentRoute.name;
            if (routeName === RouteName.offerDetailsOnlyId) {
                this.$router.replace({name: RouteName.offerDetails, params: thisItemRouteParams});
            }
            if (routeName === RouteName.archiveDetailsOnlyId) {
                this.$router.replace({name: RouteName.archiveDetails, params: archiveItemRouteParams});
            }
        },
        getLocalizedString(value: TranslatedString): string {
            return i18n.locale === 'pl' ? value.pl : value.en;
        }
    },
    metaInfo() {
        return {
            link: [
                {vmid: 'canonical', rel: 'canonical', href: location.origin + this.$route.fullPath}
            ]
        }
    },
});
